import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert, 
  Grid, 
  Card, 
  CardMedia, 
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Fab,
  Backdrop,
  Chip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PhotoCamera as PhotoCameraIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { ContactBanner, SiteFooter } from './Home';
import axios from 'axios';

interface ActivityItem {
  id: number;
  title: string;
  description?: string;
  image_url: string;
  alt_text?: string;
  order_index: number;
  category?: string;
}

interface ActivitySection {
  id: number;
  name: string;
  title: string;
  description?: string;
  section_type: 'vie_academique' | 'moments_forts';
  order_index: number;
  items: ActivityItem[];
}

const ActivityGallery = () => {
  const { sectionId: rawSectionId } = useParams<{ sectionId: string }>();
  const navigate = useNavigate();
  const [section, setSection] = useState<ActivitySection | null>(null);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [allActivities, setAllActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // États pour l'admin
  const [isAdmin, setIsAdmin] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<ActivityItem | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMethod, setUploadMethod] = useState<'upload' | 'url'>('url');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    alt_text: '',
    category: ''
  });

  // Décoder le sectionId pour gérer les caractères spéciaux comme "/"
  const sectionId = rawSectionId ? decodeURIComponent(rawSectionId) : '';

  useEffect(() => {
    checkAdminStatus();
    loadSectionData();
  }, [rawSectionId]);

  const checkAdminStatus = () => {
    const token = localStorage.getItem('token');
    const userRole = localStorage.getItem('userRole');
    setIsAdmin(!!token && userRole === 'secretary');
  };

  const loadSectionData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Charger toutes les activités
      const response = await axios.get('https://lapetiteacademie.ci/api/dynamic-pages/activities-page');
      
      if (response.data.status === 'success') {
        const sections = response.data.data.sections || [];
        
        // Collecter toutes les activités de toutes les sections
        const allActivities: ActivityItem[] = [];
        sections.forEach((section: ActivitySection) => {
          if (section.items && section.items.length > 0) {
            allActivities.push(...section.items);
          }
        });
        
        setAllActivities(allActivities);
        
        // Si sectionId est un nombre, chercher la section correspondante
        if (!isNaN(Number(sectionId)) && sectionId !== '') {
          const foundSection = sections.find((s: ActivitySection) => s.id === parseInt(sectionId || '0'));
          if (foundSection) {
            setSection(foundSection);
            setActivities(foundSection.items || []);
          } else {
            setError('Section non trouvée');
          }
        } else {
          // Si sectionId est une catégorie, filtrer par catégorie
          console.log('🔍 ActivityGallery - Filtrage par catégorie:', sectionId);
          console.log('🔍 ActivityGallery - Toutes les activités:', allActivities);
          console.log('🔍 ActivityGallery - Catégories disponibles:', [...new Set(allActivities.map(a => a.category))]);
          
          const filteredActivities = allActivities.filter(activity => {
            // Normaliser les catégories en trimant les espaces pour éviter les problèmes
            const activityCategory = (activity.category || '').trim();
            const normalizedSectionId = sectionId.trim();
            const match = activityCategory === normalizedSectionId;
            console.log(`  - ${activity.title}: "${activityCategory}" === "${normalizedSectionId}" ? ${match}`);
            return match;
          });
          
          console.log('🔍 ActivityGallery - Activités filtrées:', filteredActivities.length);
          
          if (filteredActivities.length === 0) {
            setError(`Aucune activité trouvée pour la catégorie "${sectionId}"`);
          } else {
            setActivities(filteredActivities);
            
            // Créer une section fictive pour l'affichage
            setSection({
              id: 0,
              name: 'category',
              title: `Galerie - ${sectionId}`,
              description: `Toutes les activités de la catégorie ${sectionId}`,
              section_type: 'vie_academique',
              order_index: 0,
              items: filteredActivities
            });
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imageUrl: string) => {
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    
    if (imageUrl.startsWith('/img/pages/')) {
      return `https://lapetiteacademie.ci${imageUrl}`;
    }
    
    if (imageUrl.startsWith('/uploads/pages/')) {
      return `https://lapetiteacademie.ci/api/dynamic-pages/images/${imageUrl.split('/').pop()}`;
    }
    
    if (imageUrl.startsWith('/uploads/')) {
      return `https://lapetiteacademie.ci${imageUrl}`;
    }
    
    return `https://lapetiteacademie.ci${imageUrl}`;
  };

  const handleAddItem = () => {
    setEditingItem(null);
    setFormData({
      title: '',
      description: '',
      image_url: '',
      alt_text: '',
      category: ''
    });
    setSelectedFile(null);
    setUploadMethod('url');
    setOpenDialog(true);
  };

  const handleEditItem = (item: ActivityItem) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description || '',
      image_url: item.image_url,
      alt_text: item.alt_text || '',
      category: item.category || ''
    });
    setSelectedFile(null);
    setUploadMethod('url');
    setOpenDialog(true);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Veuillez sélectionner un fichier image valide');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('La taille du fichier ne doit pas dépasser 5MB');
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const handleSubmit = async () => {
    if (!formData.title) {
      setError('Titre requis');
      return;
    }

    if (!section) {
      setError('Section non trouvée');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': uploadMethod === 'upload' ? 'multipart/form-data' : 'application/json'
      };

      let imageUrl = formData.image_url;

      if (uploadMethod === 'upload' && selectedFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('image', selectedFile);
        formDataUpload.append('title', formData.title);
        if (formData.description) formDataUpload.append('description', formData.description);
        if (formData.alt_text) formDataUpload.append('alt_text', formData.alt_text);

        const endpoint = editingItem
          ? `https://lapetiteacademie.ci/api/dynamic-pages/activities-page/items/${editingItem.id}/upload`
          : `https://lapetiteacademie.ci/api/dynamic-pages/activities-page/sections/${section.id}/items/upload`;

        const response = editingItem
          ? await axios.put(endpoint, formDataUpload, { headers })
          : await axios.post(endpoint, formDataUpload, { headers });

        if (response.data.status === 'success') {
          setSuccess(editingItem ? 'Élément mis à jour avec succès' : 'Élément ajouté avec succès');
          setOpenDialog(false);
          loadSectionData();
        }
      } else {
        const data = {
          title: formData.title,
          description: formData.description || '',
          image_url: imageUrl,
          alt_text: formData.alt_text || '',
          category: formData.category || ''
        };

        const endpoint = editingItem
          ? `https://lapetiteacademie.ci/api/dynamic-pages/activities-page/items/${editingItem.id}`
          : `https://lapetiteacademie.ci/api/dynamic-pages/activities-page/sections/${section.id}/items`;

        const response = editingItem
          ? await axios.put(endpoint, data, { headers })
          : await axios.post(endpoint, data, { headers });

        if (response.data.status === 'success') {
          setSuccess(editingItem ? 'Élément mis à jour avec succès' : 'Élément ajouté avec succès');
          setOpenDialog(false);
          loadSectionData();
        }
      }
    } catch (error: any) {
      console.error('Erreur lors de la soumission:', error);
      setError(error.response?.data?.message || 'Erreur lors de la soumission');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteItem = async (itemId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `https://lapetiteacademie.ci/api/dynamic-pages/activities-page/items/${itemId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setSuccess('Élément supprimé avec succès');
      loadSectionData();
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      setError(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !section) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', flexDirection: 'column', gap: 2 }}>
        <Alert severity="error">{error}</Alert>
        <Button variant="contained" onClick={() => navigate('/activites')}>
          Retour aux activités
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Galerie - {section?.title} | La Petite Académie</title>
        <meta name="description" content={`Découvrez toutes les images de ${section?.title} à La Petite Académie`} />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        {/* Header */}
        <Box sx={{ bgcolor: 'white', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', position: 'sticky', top: 0, zIndex: 1000 }}>
          <Container maxWidth="lg" sx={{ py: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  component={RouterLink}
                  to="/activites"
                  startIcon={<ArrowBackIcon />}
                  variant="outlined"
                  size="small"
                >
                  Retour
                </Button>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {section?.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {section?.items?.length || 0} image{(section?.items?.length || 0) > 1 ? 's' : ''}
                  </Typography>
                </Box>
              </Box>
              
              {isAdmin && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddItem}
                  size="large"
                >
                  Ajouter une image
                </Button>
              )}
            </Box>
          </Container>
        </Box>

        {/* Contenu principal */}
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {section?.description && (
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
                {section.description}
              </Typography>
            </Box>
          )}

          {section?.items.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                Aucune image dans cette section
              </Typography>
              {isAdmin && (
                <Button variant="contained" onClick={handleAddItem}>
                  Ajouter la première image
                </Button>
              )}
            </Box>
          ) : (
            <Grid container spacing={3}>
              {section?.items.map((item) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                  <Card sx={{ 
                    position: 'relative',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
                    }
                  }}>
                    <CardMedia
                      component="img"
                      height="250"
                      image={getImageUrl(item.image_url)}
                      alt={item.alt_text || item.title}
                      sx={{ objectFit: 'cover' }}
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/400x300?text=Image+non+disponible';
                      }}
                    />
                    
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" sx={{ 
                        fontSize: 16, 
                        fontWeight: 600, 
                        mb: 1,
                        lineHeight: 1.3
                      }}>
                        {item.title}
                      </Typography>
                      {item.category && (
                        <Chip
                          label={item.category}
                          color="info"
                          size="small"
                          variant="outlined"
                          sx={{ mb: 1, fontSize: 10 }}
                        />
                      )}
                      {item.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          fontSize: 13,
                          lineHeight: 1.4,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {item.description}
                        </Typography>
                      )}
                    </CardContent>

                    {isAdmin && (
                      <Box sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        display: 'flex',
                        gap: 0.5,
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                        '&:hover': {
                          opacity: 1
                        }
                      }}>
                        <IconButton
                          onClick={() => handleEditItem(item)}
                          sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': { bgcolor: 'white' }
                          }}
                          color="primary"
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteItem(item.id)}
                          sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': { bgcolor: 'white' }
                          }}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>

        {/* Bouton flottant pour admin */}
        {isAdmin && (section?.items?.length || 0) > 0 && (
          <Fab
            color="primary"
            aria-label="add"
            onClick={handleAddItem}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1000
            }}
          >
            <AddIcon />
          </Fab>
        )}

        {/* Dialog pour ajouter/modifier */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 4 } }}
        >
          <DialogTitle sx={{ fontWeight: 900, color: 'primary.main', fontSize: 24 }}>
            {editingItem ? 'Modifier l\'élément' : `Ajouter un élément à ${section?.title}`}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Titre"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description (optionnel)"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Catégorie</InputLabel>
                  <Select
                    value={formData.category}
                    label="Catégorie"
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <MenuItem value="">Sélectionner une catégorie</MenuItem>
                    <MenuItem value="Vie Académique">Vie Académique</MenuItem>
                    <MenuItem value="Moments Forts">Moments Forts</MenuItem>
                    <MenuItem value="Sorties Éducatives">Sorties Éducatives</MenuItem>
                    <MenuItem value="Activités Sportives">Activités Sportives</MenuItem>
                    <MenuItem value="Événements Spéciaux">Événements Spéciaux</MenuItem>
                    <MenuItem value="Projets Pédagogiques">Projets Pédagogiques</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Méthode d'ajout d'image</InputLabel>
                  <Select
                    value={uploadMethod}
                    label="Méthode d'ajout d'image"
                    onChange={(e) => setUploadMethod(e.target.value as 'upload' | 'url')}
                  >
                    <MenuItem value="url">URL d'image</MenuItem>
                    <MenuItem value="upload">Upload de fichier</MenuItem>
                  </Select>
                </FormControl>

                {uploadMethod === 'upload' ? (
                  <Box>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="activity-image-upload"
                      type="file"
                      onChange={handleFileSelect}
                    />
                    <label htmlFor="activity-image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<PhotoCameraIcon />}
                        fullWidth
                        sx={{ py: 2, borderStyle: 'dashed' }}
                      >
                        {selectedFile ? selectedFile.name : 'Sélectionner une image'}
                      </Button>
                    </label>
                  </Box>
                ) : (
                  <TextField
                    label="URL de l'image"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    fullWidth
                    required
                    placeholder="https://example.com/image.jpg"
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Texte alternatif (optionnel)"
                  value={formData.alt_text}
                  onChange={(e) => setFormData({ ...formData, alt_text: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 2 }}>
            <Button
              onClick={() => setOpenDialog(false)}
              variant="outlined"
              disabled={uploading}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={uploading || !formData.title || (!formData.image_url && !selectedFile)}
            >
              {uploading ? 'En cours...' : (editingItem ? 'Mettre à jour' : 'Ajouter')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbars */}
        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>

        <ContactBanner />
        <SiteFooter />
      </Box>
    </>
  );
};

export default ActivityGallery;
